import bbox from '@turf/bbox';
import bboxPolygon from '@turf/bbox-polygon';
import bufferPolygon from '@turf/buffer';
import convexHull from '@turf/convex';
import simplifyPolygon from '@turf/simplify';

import hasKinks from './kinks.js';
import {countVertexes, dedupePoints, reducePrecision} from './ops.js';
import {getArea} from './cea.js';
import {simplifyByHex} from './hex.js';

export const isValidGeometry = inGeometry => {
  // reduce the geometry's precision to planet spec's.
  const geometry = reducePrecision(inGeometry);
  // validate there are no self intersecting points or segments.
  return !hasKinks(geometry, true);
};

export const isTooComplex = (geometry, maxPoints = 500) => {
  const points = countVertexes(geometry);
  return points > maxPoints;
};

const isVeryComplex = (geometry, maxPoints = 10000) => {
  return isTooComplex(geometry, maxPoints);
};

export const isGeometryClean = (geometry, maxVertexCount) => {
  return !isTooComplex(geometry, maxVertexCount) && isValidGeometry(geometry);
};

export const createCandidates = (geometry, opts = {}) => {
  const {isValidIn, maxVertexCount = 500, includeHexClustering = false} = opts;
  const candidates = [];

  // if isValidIn is undefined, recalculate it
  const isValid =
    isValidIn === undefined ? isValidGeometry(geometry) : isValidIn;

  try {
    // check to see if a simple-dedupe will fix the geometry
    const dedupedGeometry = dedupePoints(geometry);
    if (isGeometryClean(dedupedGeometry, maxVertexCount)) {
      candidates.push({
        label: 'geometryCandidate.dedupedLabel',
        geometry: dedupedGeometry,
        description: 'geometryCandidate.dedupedDescription',
      });
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to dedupe points geometry');
  }

  const convexGeometry = convexHull(geometry).geometry;
  if (isGeometryClean(convexGeometry, maxVertexCount)) {
    candidates.push({
      label: 'geometryCandidate.convexLabel',
      geometry: convexGeometry,
      description: 'geometryCandidate.convexDescription',
    });
  }

  // The JSTS geojson parser, used by the buffer function, is also picky about geometry.
  //  if this geometry is not valid
  if (isValid) {
    const bufferGeometry = bufferPolygon(geometry, 1, {
      units: 'meters',
    }).geometry;
    if (isGeometryClean(bufferGeometry, maxVertexCount)) {
      candidates.push({
        label: 'geometryCandidate.bufferlabel',
        geometry: bufferGeometry,
        description: 'geometryCandidate.bufferDescription',
      });
    } else {
      // try to simplify it
      const simplifiedBuffer = simplifyPolygon(bufferGeometry, {
        tolerance: 0.00001,
        mutate: true,
      });
      if (isGeometryClean(simplifiedBuffer, maxVertexCount)) {
        candidates.push({
          label: 'geometryCandidate.simplifiedBufferLabel',
          geometry: simplifiedBuffer,
          description: 'geometryCandidate.simplifiedBufferDescription',
        });
      }
    }
  }

  // this round of candidates attempts to simplify by D-P
  let simplifyFound = false;

  const tolerances = [
    {
      v: 0.000001,
      label: 'geometryCandidate.toleranceLabelOneCm',
      description: 'geometryCandidate.toleranceDescriptionOneCm',
    },
    {
      v: 0.000005,
      label: 'geometryCandidate.toleranceLabelHalfM',
      description: 'geometryCandidate.toleranceDescriptionHalfM',
    },
    {
      v: 0.00001,
      label: 'geometryCandidate.toleranceLabelOneM',
      description: 'geometryCandidate.toleranceDescriptionOneM',
    },
    {
      v: 0.0001,
      label: 'geometryCandidate.toleranceLabelTenM',
      description: 'geometryCandidate.toleranceDescriptionTenM',
    },
    {
      v: 0.001,
      label: 'geometryCandidate.toleranceLabelOneHundredM',
      description: 'geometryCandidate.toleranceDescriptionOneHundredM',
    },
    {
      v: 0.01,
      label: 'geometryCandidate.toleranceLabelOneKM',
      description: 'geometryCandidate.toleranceDescriptionOneKM',
    },
    {
      v: 0.1,
      label: 'geometryCandidate.toleranceLabelTenKM',
      description: 'geometryCandidate.toleranceDescriptionTenKM',
    },
    {
      v: 1,
      label: 'geometryCandidate.toleranceLabelOneHundredKM',
      description: 'geometryCandidate.toleranceDescriptionOneHundredKM',
    },
  ];

  // D-P simplification requires a valid geometry as well.
  if (isValid && !isVeryComplex(geometry)) {
    for (let i = 0, ii = tolerances.length; !simplifyFound && i < ii; i++) {
      const simplified = simplifyPolygon(
        {...geometry},
        {highQuality: false, tolerance: tolerances[i].v, mutate: true}
      );
      if (isGeometryClean(simplified, maxVertexCount)) {
        candidates.push({
          label: tolerances[i].label,
          geometry: simplified,
          description: tolerances[i].description,
        });
        simplifyFound = true;
      }
    }
  }

  if (includeHexClustering) {
    candidates.push({
      label: 'geometryCandidate.hexSimplificationLabel',
      geometry: simplifyByHex({
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry,
          },
        ],
      }).geometry,
      description: 'geometryCandidate.hexSimplificationDescription',
    });
  }

  // bbox is a cheap replacement
  //  and requires no linting
  candidates.push({
    label: 'geometryCandidate.bboxLabel',
    geometry: bboxPolygon(bbox(geometry)).geometry,
    description: 'geometryCandidate.bboxDescription',
  });

  return candidates
    .map(candidate => {
      // annotate the area for each candidate
      return {
        ...candidate,
        area: getArea(candidate.geometry),
      };
    })
    .sort((a, b) => a.area - b.area);
};
